import { useMemo, useState, type ReactNode } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ChevronRight, PackageOpen } from 'lucide-react';
import { cn } from '@eombor/ui';
import { t } from '../../../shared/i18n/index.js';
import { CellCode, cellCodeMeaning } from './CellCode.js';
import type { LayoutCell, LayoutDto, LayoutRack } from '../api/warehouse.api.js';
import {
  cellStatusLabel,
  cellStatusTone,
  matchesCellFilter,
  type CellFilter,
} from '../model/cell-status.js';

export const rackTitle = (rack: Pick<LayoutRack, 'code' | 'name'>): string =>
  rack.name === null || rack.name === '' ? rack.code : rack.name;

/** Kataklarni qatorlarga ajratadi — javon oldida turgan odam ham shunday ko'radi. */
const byLevel = (cells: LayoutCell[]): [number, LayoutCell[]][] => {
  const levels = new Map<number, LayoutCell[]>();
  for (const cell of cells) {
    const bucket = levels.get(cell.level);
    if (bucket === undefined) levels.set(cell.level, [cell]);
    else bucket.push(cell);
  }
  return [...levels.entries()]
    .sort((a, b) => b[0] - a[0]) // Yuqori qator tepada — javonning o'zi kabi.
    .map(([level, list]) => [level, [...list].sort((a, b) => a.idx - b.idx)]);
};

export interface LayoutTreeProps {
  layout: LayoutDto;
  filter: CellFilter;
  selectedCellId: string | null;
  onSelectCell: (cellId: string) => void;
  className?: string;
}

/**
 * Yig'iladigan blok — zona ham, stellaj ham shu bilan ochilib-yopiladi.
 *
 * Balandlik `auto` ga Framer Motion bilan animatsiya qilinadi: CSS
 * `height: auto` ga o'tishni bilmaydi. `prefers-reduced-motion` da
 * animatsiya yo'q — blok shunchaki paydo bo'ladi.
 */
const Collapse = ({ open, children }: { open: boolean; children: ReactNode }) => {
  const reduce = useReducedMotion() === true;
  return (
    <AnimatePresence initial={false}>
      {open ? (
        <motion.div
          key="body"
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={reduce ? { duration: 0 } : { duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
          className="overflow-hidden"
        >
          {children}
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
};

const countBadge = (active: boolean): string =>
  cn(
    'tabular inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full px-1.5',
    'text-[0.6875rem] font-semibold',
    active ? 'bg-accent text-accent-contrast' : 'bg-surface-2 text-text-muted ring-1 ring-border',
  );

const rowButton = cn(
  'flex w-full items-center text-left outline-none',
  'transition-colors duration-[var(--motion-fast)] ease-[var(--ease-standard)]',
  'hover:bg-hover focus-visible:ring-2 focus-visible:ring-accent',
);

/**
 * Ombor tuzilmasining ro'yxat ko'rinishi: zona → stellaj → qator → katak.
 *
 * Bu 3D sahnaning **teng huquqli** alternativasi, yordamchi emas: har bir
 * katak oddiy `<button>`, ya'ni `Tab` bilan yetib boriladi va ekran o'quvchi
 * uning kodini ham holatini ham o'qiydi. 3D umuman chizilmasa ham (eski
 * kompyuter, WebGL o'chirilgan) ombor to'liq boshqarilishi kerak.
 *
 * Zona ham, stellaj ham YIG'ILADI. Yig'ilganlar bo'yicha holat saqlanadi,
 * ochilganlar bo'yicha emas: yangi ma'lumot kelganda daraxt o'z-o'zidan
 * ochiq bo'ladi. Tanlangan katak turgan zona va stellaj — FAOL yo'l —
 * urg'u rangida: chizmadan katak tanlagan odam uni ro'yxatda ham darhol
 * topadi.
 */
export const LayoutTree = ({
  layout,
  filter,
  selectedCellId,
  onSelectCell,
  className,
}: LayoutTreeProps) => {
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());

  const zones = useMemo(
    () =>
      layout.zones.map((zone) => {
        const racks = zone.racks.map((rack) => ({
          ...rack,
          visibleCells: rack.cells.filter((cell) => matchesCellFilter(cell.status, filter)),
          active: rack.cells.some((cell) => cell.id === selectedCellId),
        }));
        return {
          ...zone,
          racks,
          visibleCount: racks.reduce((sum, rack) => sum + rack.visibleCells.length, 0),
          active: racks.some((rack) => rack.active),
        };
      }),
    [layout.zones, filter, selectedCellId],
  );

  /* Zona va stellaj `id` lari alohida jadvallardan — bir xil bo'lishi
     mumkin, shuning uchun kalitga tur qo'shiladi. */
  const isOpen = (key: string): boolean => !collapsed.has(key);
  const toggle = (key: string): void => {
    setCollapsed((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {zones.map((zone) => {
        const zoneKey = `zone:${zone.id}`;
        const zoneOpen = isOpen(zoneKey);
        return (
          <section key={zone.id} aria-label={t('warehouse.zoneLabel', { name: zone.name })}>
            <button
              type="button"
              onClick={() => toggle(zoneKey)}
              aria-expanded={zoneOpen}
              className={cn(
                rowButton,
                'min-h-10 gap-2 rounded-xl px-2',
                zone.active ? 'bg-accent-soft/60' : undefined,
              )}
            >
              <ChevronRight
                size={16}
                aria-hidden
                className={cn(
                  'shrink-0 text-text-muted transition-transform duration-[var(--motion-base)]',
                  'ease-[var(--ease-standard)]',
                  zoneOpen ? 'rotate-90' : undefined,
                )}
              />
              {/* Zona rangi backendda saqlanadigan ma'lumot (`zone.color`), dizayn
                  tokeni emas — shuning uchun u yerda inline `background` o'rinli. */}
              <span
                aria-hidden
                className="size-2.5 shrink-0 rounded-full ring-2 ring-surface"
                style={{ backgroundColor: zone.color }}
              />
              <span
                className={cn(
                  'min-w-0 flex-1 truncate text-sm font-semibold',
                  zone.active ? 'text-accent' : 'text-text',
                )}
              >
                {zone.name}
                <span className="tabular ml-1.5 font-mono text-xs font-medium text-text-muted">
                  {zone.code}
                </span>
              </span>
              <span className={countBadge(zone.active)}>{zone.visibleCount}</span>
            </button>

            <Collapse open={zoneOpen}>
              {zone.racks.length === 0 ? (
                <p className="mx-2 mt-1 mb-2 flex items-center gap-2 rounded-lg border border-dashed border-border px-3 py-2 text-xs text-text-muted">
                  <PackageOpen size={14} aria-hidden className="shrink-0" />
                  {t('warehouse.zoneNoRacks')}
                </p>
              ) : (
                <ul className="flex flex-col gap-0.5 pt-0.5 pb-1 pl-3">
                  {zone.racks.map((rack) => {
                    const rackKey = `rack:${rack.id}`;
                    const open = isOpen(rackKey);
                    const title = rackTitle(rack);

                    return (
                      <li key={rack.id}>
                        <button
                          type="button"
                          onClick={() => toggle(rackKey)}
                          aria-expanded={open}
                          aria-label={t(open ? 'warehouse.rackCollapse' : 'warehouse.rackExpand', {
                            name: title,
                          })}
                          className={cn(rowButton, 'min-h-9 gap-1.5 rounded-lg px-2')}
                        >
                          <ChevronRight
                            size={14}
                            aria-hidden
                            className={cn(
                              'shrink-0 text-text-muted transition-transform',
                              'duration-[var(--motion-base)] ease-[var(--ease-standard)]',
                              open ? 'rotate-90' : undefined,
                            )}
                          />
                          <span
                            className={cn(
                              'min-w-0 flex-1 truncate text-sm',
                              rack.active ? 'font-semibold text-accent' : 'font-medium text-text',
                            )}
                          >
                            {title}
                          </span>
                          <span className={countBadge(rack.active)}>{rack.visibleCells.length}</span>
                        </button>

                        <Collapse open={open}>
                          <div
                            className={cn(
                              'ml-[1.1rem] flex flex-col gap-2 border-l-2 pt-1 pb-2.5 pl-3',
                              rack.active ? 'border-accent/50' : 'border-border',
                            )}
                          >
                            {rack.visibleCells.length === 0 ? (
                              <p className="text-xs text-text-muted">{t('warehouse.rackNoMatch')}</p>
                            ) : (
                              byLevel(rack.visibleCells).map(([level, cells]) => (
                                <div key={level} className="flex flex-col gap-1">
                                  <span className="text-[0.6875rem] font-medium tracking-wide text-text-muted uppercase">
                                    {t('warehouse.levelLabel', { level })}
                                  </span>
                                  <ul className="flex flex-wrap gap-1">
                                    {cells.map((cell) => {
                                      const selected = cell.id === selectedCellId;
                                      return (
                                        <li key={cell.id}>
                                          <button
                                            type="button"
                                            onClick={() => onSelectCell(cell.id)}
                                            aria-current={selected ? 'true' : undefined}
                                            // Nom kod BILAN holatni birga o'qiydi: ekran
                                            // o'quvchi foydalanuvchi kataka bosishdan
                                            // oldin uning bo'sh yoki bandligini biladi.
                                            // Kod BIRINCHI: skladchi uni etiketkadagi
                                            // bosma bilan solishtiradi. Ma'nosi ortidan keladi.
                                            aria-label={`${cell.code}, ${cellCodeMeaning(cell.code) ?? ''}, ${cellStatusLabel(cell.status)}`}
                                            className={cn(
                                              'inline-flex h-7 items-center rounded-md border px-2',
                                              'tabular font-mono text-xs leading-none',
                                              'transition-[background-color,border-color,color,scale]',
                                              'duration-[var(--motion-fast)] ease-[var(--ease-standard)]',
                                              'outline-none focus-visible:ring-2 focus-visible:ring-accent',
                                              'focus-visible:ring-offset-1 focus-visible:ring-offset-surface',
                                              'active:scale-[0.97]',
                                              selected
                                                ? 'border-primary bg-primary font-semibold text-primary-contrast shadow-[var(--shadow-1)]'
                                                : 'border-border bg-surface text-text-muted hover:border-border-strong hover:bg-surface-2 hover:text-text',
                                            )}
                                          >
                                            <CellCode code={cell.code} />
                                          </button>
                                        </li>
                                      );
                                    })}
                                  </ul>
                                </div>
                              ))
                            )}
                          </div>
                        </Collapse>
                      </li>
                    );
                  })}
                </ul>
              )}
            </Collapse>
          </section>
        );
      })}

      {zones.length === 0 ? (
        <p className="px-2 text-sm text-text-muted">{t('warehouse.layoutEmpty')}</p>
      ) : null}
    </div>
  );
};

/**
 * 3D sahna yonidagi `sr-only` jadval uchun yassi ro'yxat. Sahna
 * `aria-hidden` bo'lgani uchun ekran o'quvchi ma'lumotni AYNAN shu yerdan
 * oladi — shakl bir joyda hisoblanadi va ikki ko'rinish ayri ketmaydi.
 */
export interface FlatCellRow {
  id: string;
  code: string;
  zoneName: string;
  rackTitle: string;
  level: number;
  idx: number;
  statusLabel: string;
  tone: ReturnType<typeof cellStatusTone>;
}

export const flattenLayout = (layout: LayoutDto, filter: CellFilter = 'all'): FlatCellRow[] => {
  const rows: FlatCellRow[] = [];
  for (const zone of layout.zones) {
    for (const rack of zone.racks) {
      for (const cell of rack.cells) {
        if (!matchesCellFilter(cell.status, filter)) continue;
        rows.push({
          id: cell.id,
          code: cell.code,
          zoneName: zone.name,
          rackTitle: rackTitle(rack),
          level: cell.level,
          idx: cell.idx,
          statusLabel: cellStatusLabel(cell.status),
          tone: cellStatusTone(cell.status),
        });
      }
    }
  }
  return rows;
};

/** Ombordagi barcha kataklarni tez topish uchun — sahna `instanceId` dan kod topadi. */
export const findLayoutCell = (
  layout: LayoutDto,
  cellId: string | null,
): { cell: LayoutCell; rack: LayoutRack } | null => {
  if (cellId === null) return null;
  for (const zone of layout.zones) {
    for (const rack of zone.racks) {
      const cell = rack.cells.find((candidate) => candidate.id === cellId);
      if (cell !== undefined) return { cell, rack };
    }
  }
  return null;
};
